import { useState } from 'react';
import { Course, Teacher } from '@/types/data';

interface UseTeachersReturn {
  loading: boolean;
  error: string | null;
  getTeacherBySlug: (slug: string) => Promise<Teacher | undefined>;
  getTeachers: (limit?: number) => Promise<Teacher[]>;
  getCourseByTeacherSlug: (slug: string) => Promise<Course[]>;
}

export const useTeachers = (): UseTeachersReturn => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const getTeachers = async (limit?: number) => {
    try {
      setLoading(true);
      setError(null);
      const url = limit ? `/api/teachers?limit=${limit}` : '/api/teachers';
      const response = await fetch(url);
      const data = await response.json();

      if (data.success) {
        return data.data;
      } else {
        setError(data.error || 'Failed to fetch teachers');
        return [];
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch teachers');
    } finally {
      setLoading(false);
    }
    return [];
  };

  const getTeacherBySlug = async (slug: string): Promise<Teacher | undefined> => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`/api/teachers/${slug}`);
      const data = await response.json();

      if (data.success) {
        return data.data;
      } else {
        setError(data.error || 'Failed to fetch teacher');
        return undefined;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch teacher');
    } finally {
      setLoading(false);
    }
    return undefined;
  };

  const getCourseByTeacherSlug = async (slug: string): Promise<Course[]> => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`/api/courses/teacher/${slug}`);
      const data = await response.json();
      
      if (data.success) {
        return data.data;
      } else {
        setError(data.error || 'Failed to fetch courses by teacher');
        return [];
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch courses by teacher');
    } finally {
      setLoading(false);
    }
    return [];
  };

  return { loading, error, getTeacherBySlug, getTeachers, getCourseByTeacherSlug };
};